import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Phone, Mail, MessageCircle, Send, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { SITE } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORM_GROUPS = [
  {
    label: "Bewertungsportale",
    options: ["Google", "Trustpilot", "Jameda", "Kununu"],
  },
  {
    label: "Hotel- & Reiseportale",
    options: ["TripAdvisor", "HolidayCheck", "Booking.com", "HRS", "Trivago", "Expedia"],
  },
  {
    label: "Social Media (Sperrung / DSA)",
    options: ["Instagram", "TikTok", "Facebook", "YouTube", "Threads", "LinkedIn"],
  },
];

export default function ContactForm() {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    company: "",
    platform: "",
    platform_other: "",
    review_link: "",
    message: "",
    consent: false,
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const navigate = useNavigate();

  const trackWhatsAppClick = (source) => {
    if (typeof window === "undefined") return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: "whatsapp_click", source });
    if (typeof window.gtag === "function") {
      window.gtag("event", "conversion", { send_to: "AW-16867023480/7Cp6CKy784ocEPi06eo-" });
    }
  };

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Bitte stimmen Sie der Datenverarbeitung zu.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        platform: form.platform === "Andere" && form.platform_other.trim()
          ? `Andere: ${form.platform_other.trim()}`
          : form.platform,
      };
      delete payload.platform_other;
      await axios.post(`${API}/contact`, payload);
      setSent(true);
      toast.success("Vielen Dank – ich melde mich in Kürze.");
      setForm({
        first_name: "", last_name: "", email: "", phone: "", company: "",
        platform: "", platform_other: "", review_link: "", message: "", consent: false,
      });
      // Redirect to /danke (Google Ads conversion page)
      setTimeout(() => navigate("/danke"), 600);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Senden fehlgeschlagen. Bitte versuchen Sie es erneut.";
      toast.error(typeof msg === "string" ? msg : "Senden fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="kontakt" data-testid="contact-section" className="relative py-20 lg:py-28 border-t border-black/[0.05]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-16">
        <div className="lg:col-span-2">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#2b2620]">Unverbindlich anfragen</div>
          <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-[#2b2620]">
            Bewertungs­situation einschätzen lassen.
          </h2>
          <p className="mt-5 text-[#6b5f52] leading-relaxed">
            Sie haben eine negative Bewertung erhalten und möchten wissen, welche Möglichkeiten es gibt. Ich unterstütze Sie beim strukturierten Meldeprozess und gebe eine klare Ersteinschätzung.
          </p>

          <ul className="mt-7 space-y-3 text-sm text-[#4a3f36]">
            {["Unverbindliche Ersteinschätzung", "Kostenfreie Anfrage", "Transparent &amp; diskret umgesetzt", "DSGVO-konforme Abwicklung"].map((s) => (
              <li key={s} className="flex items-center gap-3">
                <CheckCircle2 className="w-4 h-4 text-[#2b2620]" />
                <span dangerouslySetInnerHTML={{ __html: s }} />
              </li>
            ))}
          </ul>

          <div className="mt-10 space-y-3">
            <a
              href={SITE.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackWhatsAppClick("contact_section")}
              data-testid="contact-whatsapp"
              data-conversion="whatsapp_click"
              className="flex items-center gap-3 p-4 rounded-2xl border border-black/[0.08] bg-[#faf6ef] hover:border-[#2b2620]/40 transition-colors"
            >
              <div className="w-10 h-10 rounded-xl bg-[#2b2620]/10 border border-[#2b2620]/30 flex items-center justify-center text-[#2b2620]">
                <MessageCircle className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-[#8a7f70]">WhatsApp</div>
                <div className="text-sm text-[#2b2620]">Direkt per WhatsApp schreiben</div>
              </div>
            </a>
            <a
              href={SITE.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackWhatsAppClick("contact_phone_link")}
              data-testid="contact-phone"
              data-conversion="whatsapp_click"
              className="flex items-center gap-3 p-4 rounded-2xl border border-black/[0.08] bg-[#faf6ef] hover:border-[#2b2620]/40 transition-colors"
            >
              <div className="w-10 h-10 rounded-xl bg-[#2b2620]/10 border border-[#2b2620]/30 flex items-center justify-center text-[#2b2620]">
                <Phone className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-[#8a7f70]">Telefon / WhatsApp</div>
                <div className="text-sm text-[#2b2620]">{SITE.phone}</div>
              </div>
            </a>
            <a
              href={`mailto:${SITE.email}`}
              data-testid="contact-email"
              className="flex items-center gap-3 p-4 rounded-2xl border border-black/[0.08] bg-[#faf6ef] hover:border-[#2b2620]/40 transition-colors"
            >
              <div className="w-10 h-10 rounded-xl bg-[#2b2620]/10 border border-[#2b2620]/30 flex items-center justify-center text-[#2b2620]">
                <Mail className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-[#8a7f70]">E-Mail</div>
                <div className="text-sm text-[#2b2620]">{SITE.email}</div>
              </div>
            </a>
          </div>
        </div>

        <div className="lg:col-span-3">
          {sent ? (
            <div data-testid="contact-success" className="rounded-3xl border border-[#2b2620]/40 bg-[#faf6ef] p-10 text-center">
              <div className="w-16 h-16 mx-auto rounded-full bg-[#2b2620]/15 flex items-center justify-center text-[#2b2620]">
                <CheckCircle2 className="w-8 h-8" />
              </div>
              <h3 className="mt-6 font-heading text-2xl font-medium text-[#2b2620]">Vielen Dank!</h3>
              <p className="mt-3 text-[#6b5f52]">
                Ihre Anfrage ist eingegangen. Ich melde mich in der Regel innerhalb von 24 Stunden bei Ihnen zurück.
              </p>
              <button
                onClick={() => setSent(false)}
                className="mt-8 inline-flex items-center justify-center px-6 py-3 rounded-full border border-black/10 text-[#2b2620] hover:bg-black/[0.03]"
                data-testid="contact-success-reset"
              >
                Neue Anfrage stellen
              </button>
            </div>
          ) : (
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="rounded-3xl border border-black/[0.08] bg-[#faf6ef] p-6 sm:p-10 space-y-5"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Vorname *" id="first_name" value={form.first_name} onChange={(v) => update("first_name", v)} required />
                <Field label="Nachname *" id="last_name" value={form.last_name} onChange={(v) => update("last_name", v)} required />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="E-Mail *" id="email" type="email" value={form.email} onChange={(v) => update("email", v)} required />
                <Field label="Telefon *" id="phone" type="tel" value={form.phone} onChange={(v) => update("phone", v)} required />
              </div>
              <Field label="Firmenname" id="company" value={form.company} onChange={(v) => update("company", v)} />

              <div>
                <label htmlFor="platform" className="block text-xs uppercase tracking-[0.18em] text-[#8a7f70] mb-2">Plattform</label>
                <select
                  id="platform"
                  data-testid="contact-field-platform"
                  value={form.platform}
                  onChange={(e) => update("platform", e.target.value)}
                  className="w-full bg-[#f5f1ea] border border-black/[0.08] rounded-xl px-4 py-3.5 text-sm text-[#2b2620] focus:outline-none focus:border-[#2b2620] transition-colors"
                >
                  <option value="">Bitte wählen…</option>
                  {PLATFORM_GROUPS.map((g) => (
                    <optgroup key={g.label} label={g.label}>
                      {g.options.map((p) => <option key={p} value={p}>{p}</option>)}
                    </optgroup>
                  ))}
                  <option value="Andere">Andere / nicht aufgeführt</option>
                </select>
                {form.platform === "Andere" && (
                  <input
                    type="text"
                    data-testid="contact-field-platform-other"
                    value={form.platform_other}
                    onChange={(e) => update("platform_other", e.target.value)}
                    placeholder="Bitte Plattform-Namen angeben…"
                    className="mt-3 w-full bg-[#f5f1ea] border border-black/[0.08] rounded-xl px-4 py-3.5 text-sm text-[#2b2620] placeholder:text-[#9b9ca5] focus:outline-none focus:border-[#2b2620] transition-colors"
                  />
                )}
              </div>

              <Field label="Link zur Bewertung" id="review_link" type="url" placeholder="https://…" value={form.review_link} onChange={(v) => update("review_link", v)} />

              <div>
                <label htmlFor="message" className="block text-xs uppercase tracking-[0.18em] text-[#8a7f70] mb-2">Kurze Hintergrundinformationen</label>
                <textarea
                  id="message"
                  data-testid="contact-field-message"
                  rows={4}
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  className="w-full bg-[#f5f1ea] border border-black/[0.08] rounded-xl px-4 py-3.5 text-sm text-[#2b2620] focus:outline-none focus:border-[#2b2620] transition-colors resize-none"
                  placeholder="Halten Sie den Link zur Bewertung gerne bereit – ich frage weitere Details direkt bei Ihnen ab."
                />
              </div>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="contact-field-consent"
                  checked={form.consent}
                  onChange={(e) => update("consent", e.target.checked)}
                  className="mt-1 w-4 h-4 accent-[#2b2620]"
                />
                <span className="text-xs text-[#6b5f52] leading-relaxed">
                  Ich willige ein, dass meine Angaben zur Bearbeitung meiner Anfrage gespeichert werden. Es entstehen keine laufenden Kosten. Die Vorab-Rückmeldung ist kostenfrei und unverbindlich.
                </span>
              </label>

              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-[#2b2620] text-white font-semibold hover:bg-[#4a4038] disabled:opacity-60 transition-colors"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {loading ? "Wird gesendet…" : "Anfrage senden"}
              </button>

              <p className="text-[11px] text-[#8a7f70] text-center">SSL-verschlüsselte Übermittlung &middot; Antwort i. d. R. innerhalb von 24 Stunden</p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function Field({ label, id, value, onChange, type = "text", required, placeholder }) {
  return (
    <div>
      <label htmlFor={id} className="block text-xs uppercase tracking-[0.18em] text-[#8a7f70] mb-2">{label}</label>
      <input
        id={id}
        data-testid={`contact-field-${id}`}
        type={type}
        required={required}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-[#f5f1ea] border border-black/[0.08] rounded-xl px-4 py-3.5 text-sm text-[#2b2620] placeholder:text-[#9b9ca5] focus:outline-none focus:border-[#2b2620] transition-colors"
      />
    </div>
  );
}
